'use client'

/**
 * PilotAvatarSet (R91.1, 2026-05-19 + Modellpilot-Styling-Update)
 *
 * 5 illustrierte männliche Modellpiloten als inline SVG für die Donor-Badges
 * und die Ring-Preview auf der Donate-Seite. Komplett DSGVO-konform
 * (kein externer API-Call, kein Asset-Download).
 *
 * Modellpilot-Styling: jeder Avatar bekommt ein typisches RC-Element —
 * Cap, Sun-Hat, Headset, Hand-Sender oder Schiebermütze — damit sofort
 * klar ist: das ist ein Modellflugpilot, kein Linienpilot oder
 * Großseglerflieger.
 *
 * Stil: freundliche Stilisierung, jeder Avatar hat distinkte Merkmale
 * (Hautton + Haare + Bart/Brille + Headwear) damit er eindeutig
 * erkennbar ist. Alle Größen skalieren über width/height-Props auf das
 * umliegende Container-Element (kein fester pixel-Wert im SVG).
 */

interface PilotAvatarProps {
  size?: number
  className?: string
}

/** Avatar 1 — Junger Modellpilot mit Baseball-Cap (FGL-Gold-Look) */
export function PilotAvatar1({ size = 80, className = '' }: PilotAvatarProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-label="Modellpilot Avatar 1">
      <circle cx="50" cy="50" r="48" fill="#fde9c5" />
      {/* Gesicht */}
      <circle cx="50" cy="58" r="22" fill="#f4c896" />
      {/* Kurze braune Haare (schauen unter Cap heraus) */}
      <path d="M 30 56 Q 30 44 36 42 L 36 50 Q 33 52 32 56 Z" fill="#4a2f1a" />
      <path d="M 70 56 Q 70 44 64 42 L 64 50 Q 67 52 68 56 Z" fill="#4a2f1a" />
      {/* Baseball-Cap — Kopfteil */}
      <path d="M 28 44 Q 28 26 50 26 Q 72 26 72 44 Q 72 46 50 46 Q 28 46 28 44 Z" fill="#1f2a44" />
      {/* Cap-Schirm */}
      <path d="M 22 46 Q 36 50 50 46 L 50 49 Q 36 53 22 49 Z" fill="#15203a" />
      {/* FGL-Gold-Streifen auf Cap */}
      <path d="M 32 35 Q 50 30 68 35" stroke="#f2c94e" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <circle cx="50" cy="36" r="2.2" fill="#f2c94e" />
      {/* Ohren */}
      <ellipse cx="29" cy="58" rx="3" ry="5" fill="#f4c896" />
      <ellipse cx="71" cy="58" rx="3" ry="5" fill="#f4c896" />
      {/* Augen */}
      <circle cx="42" cy="58" r="2.2" fill="#2a2a2a" />
      <circle cx="58" cy="58" r="2.2" fill="#2a2a2a" />
      {/* Mund (lächeln) */}
      <path d="M 43 67 Q 50 73 57 67" stroke="#a64a2a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      {/* Hals + Schulter (Polo-Shirt) */}
      <rect x="44" y="78" width="12" height="6" fill="#f4c896" />
      <path d="M 25 100 Q 25 86 50 83 Q 75 86 75 100 Z" fill="#2a4a6e" />
    </svg>
  )
}

/** Avatar 2 — Reifer Modellpilot mit Sun-Hat (breite Krempe) + Aviator-Sonnenbrille */
export function PilotAvatar2({ size = 80, className = '' }: PilotAvatarProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-label="Modellpilot Avatar 2">
      <circle cx="50" cy="50" r="48" fill="#d4e8f2" />
      {/* Gesicht */}
      <circle cx="50" cy="58" r="22" fill="#e8b890" />
      {/* Graue Haare seitlich unter Hut */}
      <path d="M 28 56 Q 28 48 32 46 L 33 54 Q 30 56 28 58 Z" fill="#9a9a9a" />
      <path d="M 72 56 Q 72 48 68 46 L 67 54 Q 70 56 72 58 Z" fill="#9a9a9a" />
      {/* Sonnen-Hut — Krone */}
      <ellipse cx="50" cy="38" rx="20" ry="10" fill="#caa874" />
      {/* Hut-Band */}
      <path d="M 30 42 Q 50 46 70 42 L 70 44 Q 50 48 30 44 Z" fill="#7a5a3a" />
      {/* Hut-Krempe (breit) */}
      <ellipse cx="50" cy="44" rx="30" ry="5" fill="#b89564" />
      {/* Ohren */}
      <ellipse cx="29" cy="58" rx="3" ry="5" fill="#e8b890" />
      <ellipse cx="71" cy="58" rx="3" ry="5" fill="#e8b890" />
      {/* Aviator-Sonnenbrille */}
      <ellipse cx="42" cy="58" rx="6.5" ry="5.5" fill="#1a1a1a" stroke="#aaa" strokeWidth="0.9" />
      <ellipse cx="58" cy="58" rx="6.5" ry="5.5" fill="#1a1a1a" stroke="#aaa" strokeWidth="0.9" />
      <line x1="48.5" y1="58" x2="51.5" y2="58" stroke="#aaa" strokeWidth="1.3" />
      {/* Reflexion in Brillengläsern (winziger heller Punkt) */}
      <circle cx="40" cy="56" r="0.9" fill="#fff" opacity="0.6" />
      <circle cx="56" cy="56" r="0.9" fill="#fff" opacity="0.6" />
      {/* Mund neutral */}
      <path d="M 44 69 Q 50 71 56 69" stroke="#7a3a1a" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      {/* Hals + Outdoor-Weste */}
      <rect x="44" y="78" width="12" height="6" fill="#e8b890" />
      <path d="M 25 100 Q 25 86 50 83 Q 75 86 75 100 Z" fill="#5a4a3a" />
    </svg>
  )
}

/** Avatar 3 — Modellpilot mit Vollbart + Over-Ear-Headset (Lärmschutz/Funk) */
export function PilotAvatar3({ size = 80, className = '' }: PilotAvatarProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-label="Modellpilot Avatar 3">
      <circle cx="50" cy="50" r="48" fill="#f5dcc4" />
      {/* Gesicht */}
      <circle cx="50" cy="55" r="22" fill="#d9a880" />
      {/* Schwarze Haare */}
      <path d="M 28 50 Q 28 26 50 26 Q 72 26 72 50 L 72 42 Q 70 32 50 32 Q 30 32 28 42 Z" fill="#1f1a14" />
      {/* Headset-Bügel über dem Kopf */}
      <path d="M 24 46 Q 24 22 50 22 Q 76 22 76 46" stroke="#1a1a1a" strokeWidth="3" fill="none" strokeLinecap="round" />
      {/* Headset-Bügel-Polster (gold-Akzent) */}
      <path d="M 38 23 Q 50 21 62 23" stroke="#f2c94e" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      {/* Linker Ohrhörer (Over-Ear-Cup) */}
      <ellipse cx="25" cy="55" rx="6" ry="9" fill="#222" stroke="#444" strokeWidth="0.8" />
      <ellipse cx="25" cy="55" rx="3.5" ry="6" fill="#3a3a3a" />
      {/* Rechter Ohrhörer */}
      <ellipse cx="75" cy="55" rx="6" ry="9" fill="#222" stroke="#444" strokeWidth="0.8" />
      <ellipse cx="75" cy="55" rx="3.5" ry="6" fill="#3a3a3a" />
      {/* Augen */}
      <circle cx="42" cy="55" r="2.2" fill="#2a2a2a" />
      <circle cx="58" cy="55" r="2.2" fill="#2a2a2a" />
      {/* Vollbart */}
      <path d="M 34 64 Q 34 78 50 80 Q 66 78 66 64 Q 60 70 50 70 Q 40 70 34 64 Z" fill="#2a2018" />
      {/* Mund unter Bart */}
      <path d="M 45 67 Q 50 69 55 67" stroke="#5a3020" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      {/* Hals */}
      <rect x="44" y="78" width="12" height="4" fill="#d9a880" />
      <path d="M 25 100 Q 25 85 50 82 Q 75 85 75 100 Z" fill="#6e2a2a" />
    </svg>
  )
}

/** Avatar 4 — Modellpilot mit Brille + Hand-Sender im Vordergrund */
export function PilotAvatar4({ size = 80, className = '' }: PilotAvatarProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-label="Modellpilot Avatar 4">
      <circle cx="50" cy="50" r="48" fill="#fff0d4" />
      {/* Gesicht */}
      <circle cx="50" cy="50" r="22" fill="#f5d4a8" />
      {/* Blonde Haare, kurz */}
      <path d="M 28 45 Q 28 25 50 25 Q 72 25 72 45 L 72 39 Q 70 33 50 33 Q 30 33 28 39 Z" fill="#d4a548" />
      <ellipse cx="29" cy="50" rx="3" ry="5" fill="#f5d4a8" />
      <ellipse cx="71" cy="50" rx="3" ry="5" fill="#f5d4a8" />
      {/* Klare Brille (rund) */}
      <circle cx="42" cy="50" r="5.5" fill="none" stroke="#2a2a2a" strokeWidth="1.4" />
      <circle cx="58" cy="50" r="5.5" fill="none" stroke="#2a2a2a" strokeWidth="1.4" />
      <line x1="47.5" y1="50" x2="52.5" y2="50" stroke="#2a2a2a" strokeWidth="1.2" />
      {/* Augen hinter Brille */}
      <circle cx="42" cy="50" r="1.5" fill="#3a5a8a" />
      <circle cx="58" cy="50" r="1.5" fill="#3a5a8a" />
      {/* Mund freundlich */}
      <path d="M 43 61 Q 50 66 57 61" stroke="#a64a2a" strokeWidth="1.8" fill="none" strokeLinecap="round" />
      {/* Hals */}
      <rect x="44" y="71" width="12" height="6" fill="#f5d4a8" />
      {/* T-Shirt */}
      <path d="M 25 100 Q 25 82 50 78 Q 75 82 75 100 Z" fill="#3a5a3a" />
      {/* RC-Hand-Sender im Vordergrund (Kasten + zwei Knüppel + Antenne) */}
      <rect x="34" y="84" width="32" height="14" rx="2" fill="#1a1a1a" stroke="#333" strokeWidth="0.8" />
      <rect x="34" y="84" width="32" height="3" fill="#2a2a2a" />
      {/* Knüppel-Sockel */}
      <circle cx="42" cy="92" r="3.5" fill="#0a0a0a" />
      <circle cx="58" cy="92" r="3.5" fill="#0a0a0a" />
      {/* Knüppel */}
      <line x1="42" y1="92" x2="42" y2="86" stroke="#666" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="58" y1="92" x2="58" y2="86" stroke="#666" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="42" cy="85.5" r="1.4" fill="#f2c94e" />
      <circle cx="58" cy="85.5" r="1.4" fill="#f2c94e" />
      {/* Antenne (kurz, oben rechts) */}
      <line x1="64" y1="84" x2="70" y2="74" stroke="#888" strokeWidth="1.2" strokeLinecap="round" />
      <circle cx="70" cy="74" r="1" fill="#f2c94e" />
    </svg>
  )
}

/** Avatar 5 — Erfahrener Modellpilot mit Schiebermütze (Flat-Cap) + Schnurrbart */
export function PilotAvatar5({ size = 80, className = '' }: PilotAvatarProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" className={className} aria-label="Modellpilot Avatar 5">
      <circle cx="50" cy="50" r="48" fill="#fbe5cd" />
      {/* Gesicht */}
      <circle cx="50" cy="58" r="22" fill="#e8b890" />
      {/* Schiebermütze (Flat-Cap) — Hauptteil */}
      <path d="M 28 44 Q 28 30 50 30 Q 72 30 72 44 Q 72 47 50 47 Q 28 47 28 44 Z" fill="#3a3528" />
      {/* Mützen-Schirm (kurz, vorne) */}
      <path d="M 24 46 Q 36 49 48 46 L 48 48 Q 36 51 24 48 Z" fill="#252018" />
      {/* Mützen-Knopf */}
      <circle cx="50" cy="32" r="1.6" fill="#5a4a3a" />
      {/* Kranz-Haare seitlich (Halb-Glatze sichtbar unter Mütze) */}
      <path d="M 28 50 Q 28 46 30 46 L 30 52 Z" fill="#787068" />
      <path d="M 72 50 Q 72 46 70 46 L 70 52 Z" fill="#787068" />
      {/* Ohren */}
      <ellipse cx="29" cy="58" rx="3" ry="5" fill="#e8b890" />
      <ellipse cx="71" cy="58" rx="3" ry="5" fill="#e8b890" />
      {/* Augen mit Falten (Lachfalten — erfahrener Pilot) */}
      <circle cx="42" cy="59" r="2" fill="#2a2a2a" />
      <circle cx="58" cy="59" r="2" fill="#2a2a2a" />
      <path d="M 36 56 Q 38 54 44 55" stroke="#a08868" strokeWidth="0.6" fill="none" />
      <path d="M 56 55 Q 62 54 64 56" stroke="#a08868" strokeWidth="0.6" fill="none" />
      <path d="M 34 64 Q 36 62 38 64" stroke="#a08868" strokeWidth="0.5" fill="none" />
      <path d="M 62 64 Q 64 62 66 64" stroke="#a08868" strokeWidth="0.5" fill="none" />
      {/* Schnurrbart */}
      <path d="M 40 67 Q 45 65 50 67 Q 55 65 60 67 Q 58 70 50 69 Q 42 70 40 67 Z" fill="#5a4a3a" />
      {/* Mund unter Schnurrbart */}
      <path d="M 45 72 Q 50 74 55 72" stroke="#7a3a1a" strokeWidth="1.4" fill="none" strokeLinecap="round" />
      {/* Hals + Holz-Fäller-Hemd */}
      <rect x="44" y="79" width="12" height="6" fill="#e8b890" />
      <path d="M 25 100 Q 25 86 50 83 Q 75 86 75 100 Z" fill="#7a2a2a" />
      {/* Karo-Muster auf Hemd (2 Linien) */}
      <line x1="40" y1="88" x2="40" y2="100" stroke="#3a1010" strokeWidth="0.6" opacity="0.6" />
      <line x1="60" y1="88" x2="60" y2="100" stroke="#3a1010" strokeWidth="0.6" opacity="0.6" />
      <line x1="28" y1="92" x2="72" y2="92" stroke="#3a1010" strokeWidth="0.6" opacity="0.6" />
    </svg>
  )
}

const AVATARS = [PilotAvatar1, PilotAvatar2, PilotAvatar3, PilotAvatar4, PilotAvatar5]

/** Wählt einen der 5 Avatare anhand Index 0-4 (mod 5). */
export function PilotAvatarByIndex({ index, size = 80, className = '' }: { index: number; size?: number; className?: string }) {
  const idx = ((index % AVATARS.length) + AVATARS.length) % AVATARS.length
  const Avatar = AVATARS[idx]
  return <Avatar size={size} className={className} />
}
